/* eslint-disable max-lines */
import { type BenefitType } from '@strata/constants';
import { type Worklist } from '@strata/react-worklist';

import { fmhAPI, OOPS_API_URL } from 'api/APIBase';
import { type MemberType } from 'services/constants';

export const postNewTestMember = ({
    payer_id,
    member_id,
    benefit_group,
    first_name,
    last_name,
    date_of_birth,
    phone,
    email,
    member_address1,
    member_address2,
    member_city,
    member_state,
    member_zip,
    carrier,
    account,
}: {
    payer_id: string;
    member_id: string | undefined;
    benefit_group: string | undefined;
    first_name: string | undefined;
    last_name: string | undefined;
    date_of_birth: string | undefined;
    phone: string | undefined;
    email: string | undefined;
    member_address1: string | undefined;
    member_address2: string | undefined;
    member_city: string | undefined;
    member_state: string | undefined;
    member_zip: string | undefined;
    carrier: string | undefined;
    account: string | undefined;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/members/${payer_id}`)
        .post({
            member_id,
            benefit_group,
            first_name,
            last_name,
            date_of_birth,
            phone,
            email,
            member_address1,
            member_address2,
            member_city,
            member_state,
            member_zip,
            carrier,
            account,
        })
        .json<object>();

export const postNewReferral = ({
    segment_id,
    payer_id,
    billing,
    product_oid,
    diagnosis_code,
    memberType,
    member_id,
    patient_first_name,
    patient_dob,
    supplier_npi,
    prescriber_npi,
    place_of_service,
    auth_start,
    auth_expiration,
    service_start_date,
    referral_start,
    referral_expiration,
    is_brand,
    grandfathered,
    toc_flag,
}: {
    segment_id: string;
    payer_id: string;
    billing: BenefitType | undefined;
    product_oid: string | undefined;
    diagnosis_code: string | undefined;
    memberType: MemberType;
    member_id: string | undefined;
    patient_first_name: string | undefined;
    patient_dob: string | undefined;
    supplier_npi: string | undefined;
    prescriber_npi: string | undefined;
    place_of_service: string | undefined;
    auth_start: string | undefined;
    auth_expiration: string | undefined;
    service_start_date: string | undefined;
    referral_start: string | undefined;
    referral_expiration: string | undefined;
    is_brand: boolean | null;
    grandfathered: boolean;
    toc_flag: boolean;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/referrals/${payer_id}/${segment_id}`)
        .post({
            member_body: member_id
                ? {
                      member_id,
                      first_name: patient_first_name,
                      date_of_birth: patient_dob,
                  }
                : {},
            referral_body: {
                segment_id,
                payer_id,
                billing,
                product_oid,
                diagnosis_code,
                memberType,
                member_id,
                patient_first_name,
                patient_dob,
                supplier_npi,
                prescriber_npi,
                place_of_service,
                auth_start,
                auth_expiration,
                service_start_date,
                referral_start,
                referral_expiration,
                is_brand,
                grandfathered,
                toc_flag,
            },
        })
        .json<object>();

export const postWorklistTask = ({
    payer_id,
    segment_id,
    benefit,
    value_case,
    product_service_oid,
    supplier_npi,
}: {
    payer_id: string;
    segment_id: string;
    benefit: BenefitType;
    value_case: { value_case_id: string; segment_id: string };
    product_service_oid: string;
    supplier_npi: string;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/worklist_tasks/create/${payer_id}/${value_case.value_case_id}`)
        .post({
            value_case,
            member_body: {},
            referral_body: {
                payer_id,
                segment_id,
                billing: benefit,
                product_oid: product_service_oid,
                supplier_npi,
            },
        })
        .json<object>();

export const getNewReferral = () => fmhAPI.base.url(`${OOPS_API_URL}/referrals`).get().json();

interface BatchConfig {
    payer_id: string;
    segment_id: string;
    low_touch_referrals: number;
    high_touch_referrals: number;
    raw_data: boolean;
}

export const postPreviewBatch = ({
    payer_id,
    segment_id,
    low_touch_referrals,
    high_touch_referrals,
    raw_data,
}: BatchConfig) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/referrals/batch/preview/${payer_id}/${segment_id}`)
        .post({
            payer_id,
            segment_id,
            low_touch_referrals,
            high_touch_referrals,
            raw_data,
        })
        .json<object>();

export const postExecuteBatch = ({
    payer_id,
    segment_id,
    low_touch_referrals,
    high_touch_referrals,
    raw_data,
}: BatchConfig) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/referrals/batch/execute/${payer_id}/${segment_id}`)
        .post({
            payer_id,
            segment_id,
            low_touch_referrals,
            high_touch_referrals,
            raw_data,
        })
        .json<object>();

export interface ServiceBusMessage {
    id: string;
    auto_renew_seconds: number;
    body: object;
    enqueued_timestamp: string;
    retry_count: number;
    service: string;
    timestamp: string;
    error?: { msg: string };
}

export const getMessagesFromDeadLetterQueue = ({
    topic,
    subscription,
    maxCount,
    minSequenceNumber,
}: {
    topic: string;
    subscription: string;
    maxCount: number;
    minSequenceNumber: number;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/topics/${topic}/subscriptions/${subscription}/deadletter`)
        .query({ max_count: maxCount, min_sequence_number: minSequenceNumber })
        .get()
        .json<{ max_sequence_number: number; messages: ServiceBusMessage[] }>();

export const deleteMessagesFromDeadLetterQueue = ({
    topic,
    subscription,
    count,
    replay,
}: {
    topic: string;
    subscription: string;
    count: number;
    replay: boolean;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/topics/${topic}/subscriptions/${subscription}/deadletter`)
        .query({ count, replay })
        .post()
        .json<void>();

export const putSetAuctionEndTime = ({
    referral_id,
    minutes_offset,
}: {
    referral_id: string;
    minutes_offset: number;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/auctions/edit/end_time`)
        .put({
            referral_id,
            minutes_offset,
        })
        .json<string>();

export const postClearTask = ({
    task_status,
    supplier_npi,
}: {
    task_status: string;
    supplier_npi: string;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/worklist_tasks/clear/${task_status}/${supplier_npi}`)
        .post()
        .json<object>();

export const lookupMemberData = ({
    payerId,
    memberId,
    firstName,
    dob,
}: {
    payerId: string;
    memberId: string;
    firstName: string;
    dob: string;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/members/lookup/${payerId}/${memberId}/${firstName}/${dob}`)
        .get()
        .json<{
            member_id: string;
            first_name: string;
            date_of_birth: string;
            last_name?: string;
            benefit_code?: string;
            benefit_group?: string;
            member_state?: string;
            member_zip?: string;
            relationship_code?: string;
        }>();

export const postNewClaim = ({
    payer_id,
    segment_id,
    claim_id,
    product_service_oid,
    product_service_id,
    supplier_npi,
    claim_date_of_service,
    adjudication_date,
    member_id,
    pt_first_name,
    pt_last_name,
    pt_dob,
    drug_prescription_number,
    place_of_service,
    claim_status,
    sequence_number,
    drug_days_supply,
    drug_quantity,
    drug_ingredient_cost,
    pt_deductible,
    pt_coinsurance,
    pt_copay,
    pt_pay_other,
}: {
    payer_id: string;
    segment_id: string;
    claim_id: string;
    product_service_oid: string;
    product_service_id: string;
    supplier_npi: string;
    claim_date_of_service: string;
    adjudication_date: string;
    member_id: string;
    pt_first_name: string;
    pt_last_name: string;
    pt_dob: string;
    drug_prescription_number: string;
    place_of_service: string;
    claim_status: string;
    sequence_number: number;
    drug_days_supply: number;
    drug_quantity: number;
    drug_ingredient_cost: number;
    pt_deductible: number | undefined;
    pt_coinsurance: number | undefined;
    pt_copay: number | undefined;
    pt_pay_other: number | undefined;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/claims/${payer_id}/${segment_id}`)
        .post({
            payer_id,
            segment_id,
            claim_id,
            product_service_oid,
            product_service_id,
            supplier_npi,
            claim_date_of_service,
            adjudication_date,
            member_id,
            pt_first_name,
            pt_last_name,
            pt_dob,
            drug_prescription_number,
            place_of_service,
            claim_status,
            sequence_number,
            drug_days_supply,
            drug_quantity,
            drug_ingredient_cost,
            pt_deductible,
            pt_coinsurance,
            pt_copay,
            pt_pay_other,
        })
        .json<object>();

export const postDeclareIncident = ({
    name,
    started_at,
    description,
    missing_quality_check,
    primary_impacted_service_name,
}: {
    name: string;
    started_at: string;
    description: string | undefined;
    missing_quality_check: string | undefined;
    primary_impacted_service_name: string | undefined;
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/incident-tracking/incidents`)
        .post({
            name,
            started_at,
            description,
            missing_quality_check,
            primary_impacted_service_name,
        })
        .json<object>();

export const postRebillingTask = ({
    payer_id,
    referral_id,
    supplier_npi,
    claim_id,
    task_type,
}: {
    payer_id: string;
    referral_id: string;
    supplier_npi: string;
    claim_id: string | undefined;
    task_type: (typeof Worklist.WorkTaskType)[keyof typeof Worklist.WorkTaskType];
}) =>
    fmhAPI.base
        .url(`${OOPS_API_URL}/worklist_tasks/add`)
        .post({
            payer_id,
            referral_id,
            supplier_npi,
            claim_id,
            task_type,
        })
        .json<object>();
